import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { api, PROPERTY_TYPES } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import SectionHeader from "@/components/SectionHeader";
import SEO from "@/components/SEO";

export default function Properties() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: params.get("type") || "",
    location: params.get("location") || "",
    intent: params.get("intent") || "",
    min: params.get("min") || "",
    max: params.get("max") || "",
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      const q = {};
      if (filters.type) q.property_type = filters.type;
      if (filters.location) q.location = filters.location;
      if (filters.intent) q.intent = filters.intent;
      if (filters.min) q.min_price = Number(filters.min);
      if (filters.max) q.max_price = Number(filters.max);
      try {
        const r = await api.get("/properties", { params: q });
        setItems(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [filters]);

  const apply = (patch) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    const nextP = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => v && nextP.set(k, v));
    setParams(nextP);
  };

  const reset = () => apply({ type: "", location: "", intent: "", min: "", max: "" });

  return (
    <>
      <SEO
        title="Properties for Sale & Buy in Kolkata and South 24 Parganas"
        description="Browse residential land, flats, apartments, independent houses and commercial spaces across Kolkata, Bawali, Budge Budge, Nodakhali, Amtala and Diamond Harbour."
        path="/properties"
        keywords="property for sale Kolkata, flats in South 24 Parganas, land plots Bawali, residential land Budge Budge, commercial property Diamond Harbour"
      />
      <section className="bg-[#101827] text-white">
        <div className="ae-container pt-28 pb-14">
          <div className="ae-eyebrow mb-3">Properties</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold">Browse our property inventory</h1>
          <p className="text-white/70 mt-3 max-w-2xl">Land, plots, flats, independent houses and commercial spaces across Kolkata and South 24 Parganas. Demo listings are clearly marked.</p>
        </div>
      </section>

      <section className="ae-section">
        <div className="ae-container">
          <div className="ae-card p-5 mb-8 flex flex-col lg:flex-row gap-3 items-stretch" data-testid="filter-bar">
            <div className="flex-1 flex items-center gap-2 border border-[#E2E8F0] rounded-lg px-3">
              <Search className="w-4 h-4 text-[#64748B]" />
              <input value={filters.location} onChange={(e) => apply({ location: e.target.value })} className="w-full py-2.5 text-sm bg-transparent focus:outline-none" placeholder="Search by location (Bawali, New Town…)" data-testid="filter-location" />
            </div>
            <select value={filters.type} onChange={(e) => apply({ type: e.target.value })} className="border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-sm bg-white" data-testid="filter-type">
              <option value="">All property types</option>
              {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select value={filters.intent} onChange={(e) => apply({ intent: e.target.value })} className="border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-sm bg-white" data-testid="filter-intent">
              <option value="">Buy or Sell</option>
              <option value="buy">For Buyers</option>
              <option value="sell">For Sellers</option>
            </select>
            <input value={filters.min} onChange={(e) => apply({ min: e.target.value })} placeholder="Min ₹" className="w-28 border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-sm" data-testid="filter-min" />
            <input value={filters.max} onChange={(e) => apply({ max: e.target.value })} placeholder="Max ₹" className="w-28 border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-sm" data-testid="filter-max" />
            <button onClick={reset} className="ae-btn-outline" data-testid="filter-reset"><SlidersHorizontal className="w-4 h-4" /> Reset</button>
          </div>

          {loading ? (
            <div className="py-20 text-center text-[#64748B]">Loading properties…</div>
          ) : items.length === 0 ? (
            <div className="py-20 text-center text-[#64748B]">No properties match these filters. Try widening your search.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((p) => <PropertyCard key={p.id} p={p} />)}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
